import {
  RESOURCE_URL_FQDN_PATTERN,
  RESOURCE_URL_IPV4_PATTERN,
  RESOURCE_NAME_VALID_PATTERN,
  RESOURCE_NAME_VALID_FIRST_CHAR_PATTERN,
  RESOURCE_URL_SCHEME_PATTERN,
  RESOURCE_MSG_LOB_REQUIRED,
  RESOURCE_MSG_NAME_REQUIRED,
  RESOURCE_MSG_RESOURCE_NAME_REQUIRED,
  RESOURCE_MSG_RESOURCE_URL_REQUIRED,
  RESOURCE_MSG_NAME_INVALID_START,
  RESOURCE_MSG_NAME_INVALID_CHARS,
  RESOURCE_MSG_URL_INVALID_SCHEME,
  RESOURCE_MSG_URL_INVALID_DOMAIN,
  RESOURCE_MSG_URL_INVALID,
  RESOURCE_MSG_NAME_DUPLICATE,
  RESOURCE_MSG_URL_DUPLICATE,
} from "../../../../../constants";

interface Resource {
  name?: string;
  url?: string;
  [key: string]: unknown;
}

export interface ResourceGroup {
  instanceId?: string;
  lob?: string;
  lobName?: string;
  name?: string;
  uiPosition?: number;
  resources?: Resource[];
  [key: string]: unknown;
}

export interface ResourceData {
  data?: ResourceGroup[];
  [key: string]: unknown;
}

interface FormData {
  lob?: string;
  name?: string;
  resourceName?: string;
  resourceUrl?: string;
  [key: string]: unknown;
}

interface RowData {
  resourceName?: string;
  resourceUrl?: string;
  itemIndex?: number;
  [key: string]: unknown;
}

/**
 * Transform JSON to one row per resource
 * @param {Array} data - Array of resource groups
 * @returns {Array} - Flattened array of resources
 */
export const transformToTableRows = (
  data: ResourceGroup[],
): Record<string, unknown>[] => {
  if (!data || !Array.isArray(data)) return [];

  return data.flatMap((group) =>
    (group.resources || []).map((resource, index) => ({
      instanceId: group.instanceId,
      lob: group.lob,
      lobName: group.lobName,
      name: group.name,
      resourceName: resource.name,
      resourceUrl: resource.url,
      uiPosition: group.uiPosition,
      itemIndex: index,
    })),
  );
};

/**
 * Get existing groups from resource data for dialog options
 * @param {Array|Object} resourcesData - Resource data (array or {data: []})
 * @returns {Array} - Array of group options
 */
export const getExistingGroups = (
  resourcesData: ResourceGroup[] | ResourceData,
): ResourceGroup[] => {
  let dataArray: ResourceGroup[] | undefined;
  if (
    resourcesData &&
    !Array.isArray(resourcesData) &&
    (resourcesData as ResourceData).data
  ) {
    dataArray = (resourcesData as ResourceData).data;
  } else if (Array.isArray(resourcesData)) {
    dataArray = resourcesData as ResourceGroup[];
  }

  if (!dataArray || !Array.isArray(dataArray)) return [];

  return dataArray.map((group) => ({
    instanceId: group.instanceId,
    lob: group.lob,
    name: group.name,
    lobName: group.lobName,
    uiPosition: group.uiPosition,
  }));
};

/**
 * Normalize resource data structure
 * @param {Array|Object} resourcesData - Resource data (array or {data: []})
 * @returns {Array} - Normalized array of resources
 */
export const normalizeResourceData = (
  resourcesData: ResourceGroup[] | ResourceData,
): Record<string, unknown>[] => {
  let dataArray: ResourceGroup[] | undefined;
  if (
    resourcesData &&
    !Array.isArray(resourcesData) &&
    (resourcesData as ResourceData).data
  ) {
    dataArray = (resourcesData as ResourceData).data;
  } else if (Array.isArray(resourcesData)) {
    dataArray = resourcesData as ResourceGroup[];
  }

  if (dataArray && Array.isArray(dataArray)) {
    return transformToTableRows(dataArray);
  }

  return [];
};

/**
 * Validate resource form data
 * @param {Object} formData - Form data to validate
 * @param {Array} resourceGroups - Array of resource groups
 * @param {boolean} isAddNew - Whether this is a new resource
 * @param {Object} row - Original row data (for edit mode)
 * @returns {Object} - Object containing validation errors
 */
export const validateResourceForm = (
  formData: FormData,
  resourceGroups: ResourceGroup[],
  isAddNew: boolean,
  row: RowData | null,
): { lob: string; name: string; resourceName: string; resourceUrl: string } => {
  const errors = {
    lob: "",
    name: "",
    resourceName: "",
    resourceUrl: "",
  };

  // Required field validations
  if (isAddNew && (!formData.lob || formData.lob.trim() === "")) {
    errors.lob = RESOURCE_MSG_LOB_REQUIRED;
  }

  if (!formData.name || formData.name.trim() === "") {
    errors.name = RESOURCE_MSG_NAME_REQUIRED;
  }

  if (!formData.resourceName || formData.resourceName.trim() === "") {
    errors.resourceName = RESOURCE_MSG_RESOURCE_NAME_REQUIRED;
  } else {
    const formatError = validateResourceNameFormat(formData.resourceName);
    if (formatError) {
      errors.resourceName = formatError;
    } else {
      const duplicateError = checkDuplicateResourceName(
        formData.resourceName,
        formData,
        resourceGroups,
        isAddNew,
        row,
      );
      if (duplicateError) {
        errors.resourceName = duplicateError;
      }
    }
  }

  if (!formData.resourceUrl || formData.resourceUrl.trim() === "") {
    errors.resourceUrl = RESOURCE_MSG_RESOURCE_URL_REQUIRED;
  } else {
    const formatError = validateResourceUrlFormat(formData.resourceUrl);
    if (formatError) {
      errors.resourceUrl = formatError;
    } else {
      const duplicateError = checkDuplicateResourceUrl(
        formData.resourceUrl,
        formData,
        resourceGroups,
        isAddNew,
        row,
      );
      if (duplicateError) {
        errors.resourceUrl = duplicateError;
      }
    }
  }

  return errors;
};

/**
 * Validate resource name format: only ASCII alphanumeric, hyphen, underscore, and spaces.
 * First character must be a letter, digit, or underscore (not a hyphen, space, or any symbol).
 * Rejects special characters, Unicode, and emojis.
 */
export const validateResourceNameFormat = (value: string): string => {
  if (!value || value.trim() === "") return "";
  const trimmed = value.trim();

  // First char: letter, digit, or underscore. Remaining: also allows hyphens and spaces.
  if (!RESOURCE_NAME_VALID_PATTERN.test(trimmed)) {
    return RESOURCE_NAME_VALID_FIRST_CHAR_PATTERN.test(trimmed)
      ? RESOURCE_MSG_NAME_INVALID_CHARS
      : RESOURCE_MSG_NAME_INVALID_START;
  }

  return "";
};

/**
 * Validate resource URL format: must be http(s) with a valid FQDN or IPv4 address.
 * Accepts internal services reachable by IP (e.g. https://10.0.0.1/tool).
 * Rejects invalid formats, bare hostnames without a dot, and non-http(s) schemes.
 */
export const validateResourceUrlFormat = (value: string): string => {
  const trimmed = value.trim();
  if (!trimmed) return "";

  if (!RESOURCE_URL_SCHEME_PATTERN.test(trimmed)) {
    return RESOURCE_MSG_URL_INVALID_SCHEME;
  }

  try {
    const parsed = new URL(trimmed);
    const hostname = parsed.hostname.toLowerCase();

    // Accept bare IPv4 addresses (e.g. https://10.0.0.1/tool)
    const isIPv4 = RESOURCE_URL_IPV4_PATTERN.test(hostname);

    // For FQDNs: must have at least one dot and a valid TLD ([a-zA-Z]{2,})
    // FQDN_PATTERN already enforces the TLD structure, so no separate TLD check needed
    if (!isIPv4 && !RESOURCE_URL_FQDN_PATTERN.test(hostname)) {
      return RESOURCE_MSG_URL_INVALID_DOMAIN;
    }
  } catch {
    return RESOURCE_MSG_URL_INVALID;
  }

  return "";
};

/**
 * Check if resource name is duplicate in the group
 * @param {string} value - Resource name to check
 * @param {Object} formData - Current form data
 * @param {Array} resourceGroups - Array of resource groups
 * @param {boolean} isAddNew - Whether this is a new resource
 * @param {Object} row - Original row data (for edit mode)
 * @returns {string} - Error message or empty string
 */
export const checkDuplicateResourceName = (
  value: string,
  formData: FormData,
  resourceGroups: ResourceGroup[],
  isAddNew: boolean,
  row: RowData | null,
): string => {
  const trimmed = value.trim();
  if (trimmed !== "" && formData.name && formData.lob) {
    const currentGroup = resourceGroups.find(
      (g) => g.lob === formData.lob && g.name === formData.name,
    );

    if (currentGroup && Array.isArray(currentGroup.resources)) {
      const isDuplicate = currentGroup.resources.some((resource, index) => {
        // For edit mode, exclude the current resource being edited
        if (
          !isAddNew &&
          row?.resourceName === resource.name &&
          row?.itemIndex === index
        ) {
          return false;
        }
        return (
          (resource?.name || "").trim().toLowerCase() === trimmed.toLowerCase()
        );
      });

      if (isDuplicate) {
        return RESOURCE_MSG_NAME_DUPLICATE;
      }
    }
  }
  return "";
};

/**
 * Check if resource URL is duplicate in the group
 * @param {string} value - Resource URL to check
 * @param {Object} formData - Current form data
 * @param {Array} resourceGroups - Array of resource groups
 * @param {boolean} isAddNew - Whether this is a new resource
 * @param {Object} row - Original row data (for edit mode)
 * @returns {string} - Error message or empty string
 */
export const checkDuplicateResourceUrl = (
  value: string,
  formData: FormData,
  resourceGroups: ResourceGroup[],
  isAddNew: boolean,
  row: RowData | null,
): string => {
  const trimmedUrl = value.trim();
  if (trimmedUrl !== "" && formData.lob && formData.name) {
    const currentGroup = resourceGroups.find(
      (g) => g.lob === formData.lob && g.name === formData.name,
    );
    if (currentGroup && Array.isArray(currentGroup.resources)) {
      const isDuplicateUrl = currentGroup.resources.some((resource, index) => {
        if (
          !isAddNew &&
          row?.resourceUrl === resource.url &&
          row?.itemIndex === index
        ) {
          return false;
        }
        return (
          (resource?.url || "").trim().toLowerCase() ===
          trimmedUrl.toLowerCase()
        );
      });
      if (isDuplicateUrl) {
        return RESOURCE_MSG_URL_DUPLICATE;
      }
    }
  }
  return "";
};

/**
 * Get unique LOBs from group options
 * @param {Array} groupOptions - Array of group options
 * @returns {Array} - Array of unique LOB values
 */
export const getUniqueLobs = (groupOptions: ResourceGroup[]): string[] => {
  return [
    ...new Set(
      groupOptions
        .map((g) => g.lob)
        .filter((lob): lob is string => lob != null),
    ),
  ];
};

/**
 * Get names filtered by selected LOB
 * @param {Array} groupOptions - Array of group options
 * @param {string} selectedLob - Selected LOB value
 * @returns {Array} - Array of names for the selected LOB
 */
export const getFilteredNames = (
  groupOptions: ResourceGroup[],
  selectedLob: string,
): (string | undefined)[] => {
  return groupOptions.filter((g) => g.lob === selectedLob).map((g) => g.name);
};
